import CustomModal from "../../../components/CustomModal";
import React, { useCallback, useEffect, useState } from "react";
import {
  DYNAMIC_UI_TYPE_LIST,
  UI_BRAND_TYPE_DAY,
  UI_PRODUCT_TYPE_CATEGORY,
  UI_STATIC_TYPE_BANNER,
  UI_STATIC_TYPE_PREDICTION,
  UI_TYPE_BRAND,
  UI_TYPE_FOCUSED, UI_TYPE_GROUP, UI_TYPE_NORMAL,
  UI_TYPE_PRODUCT
} from "../constants";
import { getNonDefaultGroupedItems, getNonDefaultItems } from "../../../helpers/helperFunctions";
import LabeledSelect from "../../../components/selects/LabeledSelect";
import FocusedSelect, { FocusedSearchSelect } from "../../../components/selects/FocusedSelect";
import SpecialBrandSelect from "../../../components/selects/SpecialBrandsSelect";
import CategorySearchSelect from "../../../components/selects/CategorySearchSelect";
import CustomButton, { IconButton, OutlinedButton } from "../../../components/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { addItems, resetAddItems, resetSearchResult } from "../../../redux/slices/dynamicUiSlice";
import useDidMount from "../../../hooks/useDidMount";
import getDynamicItemFor from "../selected/DynamicItemMapper";
import { cloneDeep } from "lodash";
import CustomIcon from "../../../components/CustomIcon";
import FormLabel from "../../../components/form/FormLabel";
import CustomMessage from "../../../components/CustomMessage";
import PlatformSelector from "./PlatformSelector";
import { PLATFORM_MOBILE, PLATFORM_WEB, PLATFORMS } from "../../../constants/commons";
import { getUiBreadCrumbFormat, isMobilePlatform, isWebPlatform } from "../dynamicUiFunctions";
import PlatformToggle from "../../../components/PlatformToggle";
import EmptyState from "../../../components/EmptyState";
import BannerSelect from "components/selects/BannerSelect";
import PredictionSelect from "components/selects/PredictionSelect";
import { useNavigate } from "react-router";

const AddItemModal = ({ setShowModal, showModal }) => {

  const { loading, success } = useSelector((state) => state.dynamicUi.addItems);
  const { data: items } = useSelector((state) => state.dynamicUi.items);

  const [uiType, setUiType] = useState();
  const [ext, setExt] = useState();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [uiSubType, setUiSubType] = useState();
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [currentPlatforms, setCurrentPlatforms] = useState([]);
  const [data, setData] = useState();
  const [mobileView, setMobileView] = useState(true);

  const clearAll = useCallback(
    () => {
      dispatch((resetAddItems()));
      setUiType(undefined);
      setUiSubType(undefined);
      setData(undefined);
      setCurrentPlatforms([]);
      setExt(undefined);
    },
    [dispatch]
  );

  const presetSelectedPlatform = useCallback(
    (platforms) => {
      setSelectedPlatforms(platforms);
      if (platforms.length === 1 && ((platforms[0].value === PLATFORM_MOBILE && !mobileView) || (platforms[0].value === PLATFORM_WEB && mobileView))) {
        setMobileView(!mobileView);
      }
    },
    [dispatch, mobileView]
  );

  useEffect(() => {
    if (showModal?.edit) {
      const type = DYNAMIC_UI_TYPE_LIST.find((i) => i.value === showModal.type);
      if ((isMobilePlatform(showModal.platform) && !mobileView) || (isWebPlatform(showModal.platform) && mobileView)) {
        setMobileView(!mobileView);
      }
      if (type) {
        setUiType(type);
        const platform = showModal.platform === PLATFORM_WEB ? [PLATFORMS.web] : showModal.platform === PLATFORM_MOBILE ? [PLATFORMS.mobile] : Object.values(PLATFORMS);
        setSelectedPlatforms(platform);
        setCurrentPlatforms(platform);
        const subType = type.sub_types.find((i) => i.value === showModal.sub_type);
        if (subType) {
          setUiSubType(subType);
          if (showModal.value) {
            setExt(showModal.value);
          }
        }
      }
    } else if (showModal) {
      setSelectedPlatforms(Object.values(PLATFORMS));
    }
  }, [showModal]);

  useEffect(() => {
    return () => {
      clearAll();
    };
  }, [clearAll]);

  const closeModal = () => {
    setShowModal(undefined);
    clearAll();
  };

  useDidMount(() => {
    if (success) {
      closeModal();
    }

  }, [success]);

  const isGroupType = (type) => (type?.value ?? uiType?.value) === UI_TYPE_GROUP;
  const isFormEditType = showModal?.edit;

  const updateType = useCallback((type) => {
    if (type !== uiType) {
      setUiSubType(undefined);
      setData(undefined);
      setUiType(type);
      if (isGroupType(type)) {
        setData([{}, {}]);
      }
    }

  }, [uiType]);

  const updateSubType = useCallback((subType) => {
    if (subType !== uiSubType) {
      if (!isGroupType()) {
        setData(undefined);
      }

      setUiSubType(subType);
    }

  }, [uiSubType, uiType]);

  const submitAddNewSection = () => {
    dispatch(addItems({ uiType, uiSubType, data, formData: showModal, platforms: selectedPlatforms }));
  };

  const navigateToElement = () => {
    setShowModal()
    dispatch(resetSearchResult())
    setTimeout(() => {
      showModal.type === UI_TYPE_FOCUSED
        ? navigate(`/focus-items?id=${showModal.value}`, { replace: true })
        : showModal.sub_type === UI_STATIC_TYPE_BANNER
          ? navigate(`/banners?id=${showModal.value}`, { replace: true })
          : navigate(`/predictions?id=${showModal.value}`, { replace: true })
    }, 0)
  }

  const isFormValid = uiType && uiSubType && data && !isGroupType();
  // const isFormValid = uiType && uiSubType && data && (!isGroupType() || !data.some((d) => !d.uiType || !d.uiSubType || !d.data));

  const isPlatformUpdated = currentPlatforms.length !== selectedPlatforms.length || !selectedPlatforms.every((sp) => currentPlatforms.some((cp) => cp.value === sp.value));
  const isAlreadyAdded = isFormValid && !isGroupType() &&
    items?.some((item) => item.type === uiType?.value && item.sub_type === uiSubType?.value && item.value === data?.id);

  return (
    <CustomModal showModal={showModal} setShowModal={closeModal}
      preFooterComponent={(uiType && uiSubType && !!data?.id && selectedPlatforms.length > 1) &&

        <PlatformToggle mobileView={mobileView} mobileOnClick={() => setMobileView(true)}
          webOnClick={() => setMobileView(false)} />}
      title={isFormEditType ? 'Update Dynamic items' : 'Create Dynamic items'} loading={loading}
      button={<div className="flex space-x-2">
        {isFormEditType && (showModal.type === UI_TYPE_FOCUSED || showModal.type === UI_TYPE_NORMAL) &&
          <CustomButton onClick={navigateToElement} disabled={loading || !isFormValid || (isAlreadyAdded && (!isPlatformUpdated || !isFormEditType))}>Navigate Element</CustomButton>
        }
        <CustomButton onClick={submitAddNewSection} disabled={loading || !isFormValid || (isAlreadyAdded && (!isPlatformUpdated || !isFormEditType))}>
          {isFormEditType ? 'Update' : 'Add'}</CustomButton>


      </div>
      }
      bodyClasses="max-w-screen-md">
      {isGroupType() &&
        <CustomMessage message="Adding group is disabled from here." messageType="error" className="mb-2" />
      }
      <AddItemForm data={data} loading={loading} setData={setData} uiType={uiType} uiSubType={uiSubType} extData={ext}
        updateSubType={updateSubType} updateType={updateType}
        extraComponent={<PlatformSelector platforms={selectedPlatforms}
          setPlatform={presetSelectedPlatform} disabled={loading} />} />


      {(uiType && uiSubType && !!data?.id) &&
        <div className={`flex flex-col ${mobileView ? 'mobile' : 'web'}`}>
          <div className="flex flex-col justify-between lg:flex-row">
            <FormLabel label={`Preview (${getUiBreadCrumbFormat({
              type: uiType.value,
              sub_type: uiSubType.value,
              value: data?.id
            })})`} />
            {isAlreadyAdded &&
              <CustomMessage message="This item has already been added." hasBackground={false} className="mb-2" />}
          </div>
          <div
            className="relative flex items-center justify-center pb-4 overflow-hidden bg-white border-2 border-dashed border-rand-100 rounded-md">
            <div className={mobileView ? 'w-96' : 'w-full'}>
              {getDynamicItemFor({
                type: uiType.value,
                sub_type: uiSubType.value,
                value: data?.id,
                extras: data?.extras,
                display_type: data?.display_type
              }, { mobile: mobileView })}
            </div>
          </div>


        </div>
      }


    </CustomModal>
  );
};

export default React.memo(AddItemModal);

const AddItemForm = React.memo(({
  uiSubType,
  uiType,
  loading,
  setData,
  data,
  extraComponent,
  extData,
  updateSubType,
  updateType,
  grouped = true,
  ...props
}) => {
  const updateGroupType = (datum, index, type) => {
    if (type?.value !== datum?.uiType?.value) {
      const tempData = cloneDeep(data);
      tempData[index] = { ...datum, uiType: type, uiSubType: undefined };
      setData(tempData);
    }

  };

  const updateGroupSubType = (datum, index, subType) => {

    if (subType?.value !== datum?.uiSubType?.value) {
      const tempData = cloneDeep(data);
      tempData[index] = { ...datum, uiSubType: subType, data: undefined };
      setData(tempData);

    }

  };

  const updateGroupData = (datum, index, groupData) => {

    if (groupData?.id !== datum?.data?.id) {
      const tempData = cloneDeep(data);
      tempData[index] = { ...datum, data: groupData };

      setData(tempData);

    }

  };

  const deleteGroupData = (index) => {

    const tempData = cloneDeep(data);
    tempData.splice(index, 1);

    setData(tempData);


  };

  const addGroupData = () => {

    const tempData = cloneDeep(data);
    tempData.push({});

    setData(tempData);


  };


  return (
    <>
      <div className="flex flex-col md:space-x-4 md:flex-row" {...props}>
        <LabeledSelect options={grouped ? getNonDefaultItems(DYNAMIC_UI_TYPE_LIST) : getNonDefaultGroupedItems()}
          label="Type" onChange={updateType}
          isDisabled={loading} value={uiType} className="w-full mb-4 space-y-1 md:mb-0" />
        <LabeledSelect options={getNonDefaultItems(uiType?.sub_types)} label={uiType?.name ? uiType?.name : 'Sub Type'}
          onChange={updateSubType}
          isDisabled={loading} value={uiSubType} emptyMsg={uiType ? undefined : 'Select type first'}
          className="w-full space-y-1" />
      </div>
      {(uiType && uiSubType) &&
        <div className="">
          {uiType.value !== UI_TYPE_GROUP &&
            <>
              {!!extraComponent && extraComponent}
              <FormLabel label={`Choose ${uiSubType?.label} for ${uiType?.label}`} />
            </>
          }
          {
            uiType.value === UI_TYPE_BRAND ?
              <SpecialBrandSelect isBrandDay={uiSubType.value === UI_BRAND_TYPE_DAY}
                isDisabled={loading} onChange={setData} value={data} extData={extData} />
              : uiType.value === UI_TYPE_FOCUSED ?
                <FocusedSearchSelect isDisabled={loading} type={uiSubType.value} onChange={setData} value={data} placeholder="Start typing to search the elements."
                  extData={extData} />
                : uiType.value === UI_TYPE_PRODUCT ?
                  (uiSubType.value === UI_PRODUCT_TYPE_CATEGORY ?
                    <CategorySearchSelect isDisabled={loading} onChange={setData} value={data} extData={extData} /> :
                    <EmptyState
                      message="UnImplemented" isSmall={true} hasBackground={true} icon="priority_high"
                    />)
                  : uiType.value === UI_TYPE_GROUP ?
                    <>
                      {!!extraComponent && extraComponent}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 col-span-2">
                        {data?.map((datum, index) =>
                          <div key={`group-display-type-${index}`}
                            className="relative px-4 pt-4 pb-1 border bg-secondary-100 rounded-md">
                            {data.length > 2 &&
                              <IconButton className="absolute top-2 right-2"
                                onClick={() => deleteGroupData(index)}
                              />
                            }
                            <AddItemForm loading={loading}
                              uiType={datum?.uiType} updateType={(type) => updateGroupType(datum, index, type)}
                              uiSubType={datum?.uiSubType}
                              setData={(data) => updateGroupData(datum, index, data)}
                              data={datum?.data} grouped={false}
                              updateSubType={(subType) => updateGroupSubType(datum, index, subType)} />
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end pt-4 mt-4 text-right border-t-2 border-dashed">
                        <OutlinedButton onClick={addGroupData}>+ Add more</OutlinedButton></div>
                    </>
                    : uiType.value === UI_TYPE_NORMAL ?
                      uiSubType.value === UI_STATIC_TYPE_PREDICTION
                        ? <div>
                          <PredictionSelect extData={extData} value={data} onChange={setData} />
                        </div>
                        : <>
                          <div>
                            <BannerSelect isDisabled={loading} onChange={setData} value={data} extData={extData}
                              excludesDefault={true} />
                          </div>
                        </>
                      : <EmptyState
                        message="UnImplemented" isSmall={true} hasBackground={true} icon="priority_high"
                      />
          }

        </div>
      }
    </>
  );
});
